// @ts-ignore
import { isBetEligible } from '@winamax/missions-lib';

import { StateType } from '../../../states';
import { BasketItems, type BasketState } from '../../../states/basket';
import { type BasketItemState } from '../../../states/basket';
import { EntitiesMatches, type MatchState } from '../../../states/entities/matches';
import { BasketModes, BasketStatuses } from '../../../types/basket';
import MatchUtils from '../../../utils/match';
import { selectGRGlobalEligibility, selectGRGlobalEndDate } from '../selectors/global/globalPrimary.selector';
import { selectGRUserCurrentRound } from '../selectors/round.selector';
import { selectIsGREnabledAndNotForbidden } from '../selectors/runtime.selector';
import { selectGRUserBetslips, selectIsGRUserOptIn } from '../selectors/user/userPrimary.selector';
import GRAdventure from '../states/user';

const getIsBasketItemReadyForEligibilityCheck = ({
  globalState,
  basketItem,
  matchesState = {},
  operationEndDate,
}: {
  globalState: StateType;
  basketItem?: BasketItemState;
  matchesState?: EntitiesMatches;
  operationEndDate?: number;
}) => {
  const { basket, entities, gr } = globalState;
  const eligibility = selectGRGlobalEligibility(globalState);
  const isUserOptIn = selectIsGRUserOptIn(globalState);
  const { matchId: basketItemMatchId } = basketItem ?? {};
  const match = basketItemMatchId ? matchesState[basketItemMatchId] : undefined;
  const startsWithinPeriod = MatchUtils.isMatchStartWithin48OrEndDate(match?.matchStart, operationEndDate);

  if (!isUserOptIn) {
    return false;
  }

  // On check si le match du panier est bien dans la période de l'opération ou < 48h
  if (!startsWithinPeriod) {
    return false;
  }

  const hasEnoughPoints = GRAdventure.hasEnoughPoints(gr?.user, gr?.rounds);

  if (hasEnoughPoints) {
    return false;
  }

  const currentRound = selectGRUserCurrentRound(globalState);
  const userBetslips = selectGRUserBetslips(globalState);

  const hasCurrentRoundAnyPendingBetslipWithSameMatchId = basketItemMatchId
    ? GRAdventure.hasRoundAnyPendingBetslipWithSameMatchId({
        round: currentRound,
        matchId: basketItemMatchId,
        userBetslips,
      })
    : false;

  if (hasCurrentRoundAnyPendingBetslipWithSameMatchId) {
    return false;
  }

  const eligibilityOptions = {
    basket,
    entities,
    item: basketItem ?? null,
  };

  // On retourne true si le pari est éligible aux critères de l'opé
  return isBetEligible({
    eligibility,
    ...eligibilityOptions,
  });
};

const getIsBasketReadyForEligibilityCheck = ({
  globalState,
  nextBasketState: { items: basketItems } = {} as BasketState,
  matchesState,
  operationEndDate,
}: {
  globalState: StateType;
  nextBasketState?: BasketState;
  matchesState?: { [matchId: string]: MatchState };
  operationEndDate?: number;
}) => {
  const basketItemIds = Object.keys(basketItems);

  if (!basketItemIds.length) {
    return false;
  }

  return basketItemIds.every((itemId) =>
    getIsBasketItemReadyForEligibilityCheck({
      globalState,
      basketItem: basketItems[itemId] ?? {},
      matchesState,
      operationEndDate,
    }),
  );
};

const recomputeNextGRBasketState = (nextBasketState: BasketState, globalState: StateType) => {
  const { isCashout, status: basketStatus } = nextBasketState;
  const isGREnabled = selectIsGREnabledAndNotForbidden(globalState);
  const operationEndDate = selectGRGlobalEndDate(globalState);
  const basketMode = nextBasketState.mode;

  if (!isGREnabled || isCashout || basketStatus === BasketStatuses.VALIDATED) {
    return nextBasketState;
  }

  const { entities: { matches: matchesState = {} } = {}, basket: basketState = {} } = globalState ?? {};

  const tmpBasketState = nextBasketState ?? basketState;
  const isBasketReadyForEligibilityCheck = getIsBasketReadyForEligibilityCheck({
    globalState,
    nextBasketState,
    matchesState,
    operationEndDate,
  });

  const hasBasketGRActiveChanged = tmpBasketState.isGRActive !== isBasketReadyForEligibilityCheck;
  let hasBasketItemsChanged = false;

  const { items: basketItems } = tmpBasketState;
  const resetedBasketItems: BasketItems = {};

  for (const itemId of Object.keys(basketItems)) {
    resetedBasketItems[itemId] = basketItems[itemId];
  }

  const nextItems = { ...resetedBasketItems };

  const sortingCallback = (a: BasketItemState, b: BasketItemState) => (a.sortBy ?? -1) - (b.sortBy ?? -1);

  const basketItemsAsArr = Object.keys(resetedBasketItems)
    .map((itemId) => resetedBasketItems[itemId])
    .sort(sortingCallback);

  for (const basketItem of basketItemsAsArr) {
    const { oddId: itemId } = basketItem;

    const isBasketItemGREligible = operationEndDate
      ? getIsBasketItemReadyForEligibilityCheck({
          globalState,
          basketItem,
          matchesState,
          operationEndDate,
        })
      : false;

    if (basketMode === BasketModes.SIMPLE) {
      if (basketItem.isGRActive !== isBasketItemGREligible) {
        nextItems[itemId] = {
          ...nextItems[itemId],
          isGRActive: isBasketItemGREligible,
        };

        hasBasketItemsChanged = true;
      }
    } else {
      if (nextItems[itemId].isGRActive !== isBasketItemGREligible) {
        nextItems[itemId].isGRActive = isBasketItemGREligible;
        hasBasketItemsChanged = true;
      }
    }
  }

  let nextState = tmpBasketState;

  if (hasBasketGRActiveChanged) {
    nextState = { ...nextState, isGRActive: isBasketReadyForEligibilityCheck };
  }

  if (hasBasketItemsChanged) {
    nextState = { ...nextState, items: nextItems };
  }

  return nextState;
};

export default recomputeNextGRBasketState;
